import { Stack } from '@mui/material';
import { FlowConditionalCaseSensitivity, type FlowConditionalCondition } from 'flow-schema';
import { type ReactNode } from 'react';

import { Text, api, isDefined, isTruthy } from 'common';

import { CASE_SENSITIVITY_OPTIONS } from './constants';
import { useConditionLabel } from './useConditionLabel';
import { BlockCheckboxField } from '../../ConfigFields/Checkbox';
import { BlockValueTemplateField } from '../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockRadioGroupField } from '../../ConfigFields/RadioGroupField';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

interface ConditionCardValuesProps {
  fieldNamePrefix: string;
}

export function ConditionCardValues(props: ConditionCardValuesProps) {
  const { fieldNamePrefix } = props;
  const { value: condition } = useSelectedNodeField<FlowConditionalCondition>(fieldNamePrefix);
  const { updateConditionLabel } = useConditionLabel({ fieldNamePrefix });
  const dataType = condition?.dataType;
  const operation = condition?.operation;
  if (!isDefined(dataType) || !isDefined(operation)) {
    return null;
  }
  const supportedValues =
    api.FlowDefinition.Conditional.VALUES_SUPPORTED_BY_DATA_TYPE[dataType]?.[operation];
  if (!isTruthy(supportedValues?.length)) {
    return null;
  }

  const components: ReactNode[] = [];
  if (supportedValues.includes('value')) {
    components.push(
      <BlockValueTemplateField key="value" fieldName={`${fieldNamePrefix}.value`} label="Value" />
    );
  }
  if (supportedValues.includes('caseSensitivity')) {
    components.push(
      <BlockRadioGroupField
        key="caseSensitivity"
        fieldName={`${fieldNamePrefix}.caseSensitivity`}
        label={<Text variant="heading6">Case Sensitivity</Text>}
        options={CASE_SENSITIVITY_OPTIONS}
        row
        defaultValue={FlowConditionalCaseSensitivity.ExactMatch}
      />
    );
  }
  if (supportedValues.includes('minValue')) {
    components.push(
      <BlockValueTemplateField
        key="minValue"
        fieldName={`${fieldNamePrefix}.minValue`}
        label="Minimum Value"
        onUpdate={(value) => {
          updateConditionLabel({
            minValue: parseFloat(value),
          });
        }}
      />
    );
  }
  if (supportedValues.includes('minValueInclusive')) {
    components.push(
      <BlockCheckboxField
        key="minValueInclusive"
        fieldName={`${fieldNamePrefix}.minValueInclusive`}
        label="Include minimum in the range."
        onChange={(minValueInclusive) => {
          updateConditionLabel({ minValueInclusive });
        }}
      />
    );
  }
  if (supportedValues.includes('maxValue')) {
    components.push(
      <BlockValueTemplateField
        key="maxValue"
        fieldName={`${fieldNamePrefix}.maxValue`}
        label="Maximum Value"
        onUpdate={(value) => {
          updateConditionLabel({
            maxValue: parseFloat(value),
          });
        }}
      />
    );
  }
  if (supportedValues.includes('maxValueInclusive')) {
    components.push(
      <BlockCheckboxField
        key="maxValueInclusive"
        fieldName={`${fieldNamePrefix}.maxValueInclusive`}
        label="Include maximum in the range."
        onChange={(maxValueInclusive) => {
          updateConditionLabel({ maxValueInclusive });
        }}
      />
    );
  }
  return <Stack gap={1}>{components}</Stack>;
}
